import { Card } from '@azyntor-labs/nexus-ui';
import { Activity, Globe, Laptop, Shuffle } from 'lucide-react';
import type { AttemptFullDetailTimingStats } from '@/api/endpoints';

interface Props {
  timing: AttemptFullDetailTimingStats;
  patterns: {
    ip_address?: string;
    user_agent?: string;
    device_fingerprint?: Record<string, unknown>;
    shuffled_order: boolean;
  };
}

const bucketLabels = ['<5s', '5-10s', '10-15s', '15-30s', '30-60s', '60s+'];

function parseUA(ua?: string) {
  if (!ua) return { browser: '—', os: '—' };
  let browser = '—';
  let os = '—';
  if (/Chrome\/([\d.]+)/.test(ua)) browser = `Chrome ${RegExp.$1.split('.')[0]}`;
  else if (/Firefox\/([\d.]+)/.test(ua)) browser = `Firefox ${RegExp.$1.split('.')[0]}`;
  else if (/Safari\/([\d.]+)/.test(ua)) browser = `Safari`;
  else if (/Edg\/([\d.]+)/.test(ua)) browser = `Edge ${RegExp.$1.split('.')[0]}`;
  if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  return { browser, os };
}

export function PatternsPanel({ timing, patterns }: Props) {
  const max = Math.max(1, ...timing.histogram);
  const { browser, os } = parseUA(patterns.user_agent);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Timing histogram */}
      <Card className="md:col-span-2">
        <div className="p-5 border-b border-white/5">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Activity className="w-4 h-4" /> Answer timing distribution
          </h3>
          <p className="text-xs text-white/40 mt-1">
            {timing.answered_count > 0
              ? `min ${timing.min_seconds}s · median ${timing.median_seconds}s · max ${timing.max_seconds}s · ${timing.fast_answer_count} answered in <3s`
              : 'No timing data yet.'}
          </p>
        </div>
        <div className="p-5">
          {timing.answered_count === 0 ? (
            <p className="text-sm text-white/40 italic">Not enough data.</p>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {timing.histogram.map((count, i) => {
                const h = Math.max(4, (count / max) * 100);
                const isFast = i === 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="flex-1 flex items-end w-full">
                      <div
                        className={`w-full rounded-t ${isFast && count > 0 ? 'bg-red-400/70' : 'bg-teal-400/60'}`}
                        style={{ height: `${h}%` }}
                        title={`${count} answer(s)`}
                      />
                    </div>
                    <div className="text-[10px] text-white/50">{bucketLabels[i]}</div>
                    <div className="text-[10px] text-white/80 font-medium">{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Identity / environment */}
      <Card>
        <div className="p-5 border-b border-white/5">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Laptop className="w-4 h-4" /> Environment
          </h3>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row icon={<Globe className="w-3.5 h-3.5" />} label="IP" value={patterns.ip_address || '—'} />
          <Row icon={<Laptop className="w-3.5 h-3.5" />} label="Browser" value={browser} />
          <Row icon={<Laptop className="w-3.5 h-3.5" />} label="OS" value={os} />
          <Row
            icon={<Shuffle className="w-3.5 h-3.5" />}
            label="Question order"
            value={patterns.shuffled_order ? 'Shuffled per student' : 'Fixed order'}
          />
          {patterns.device_fingerprint && Object.keys(patterns.device_fingerprint).length > 0 && (
            <details className="text-xs text-white/60">
              <summary className="cursor-pointer text-white/70">Device fingerprint</summary>
              <pre className="mt-2 p-2 rounded bg-black/30 border border-white/[0.06] text-[10px] overflow-x-auto">
                {JSON.stringify(patterns.device_fingerprint, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </Card>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-white/50">
        {icon} {label}
      </span>
      <span className="text-white/90 font-mono text-xs text-right truncate max-w-[60%]" title={value}>
        {value}
      </span>
    </div>
  );
}
